import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { createSyntheticPageViewEvent } from '../utility/dataLayerUtils';
import { datadogRum } from './useDatadog';

// Persists across route changes but resets on page refresh
const hasNavigatedBefore = { value: false };

// Synthetic pageview mirroring GA4, same behavior as PLIEDU-FrontEnd's EnhancedRouteTracker.
// Single push to window.dataLayer: GTM consumes it for GA4, and the index.html dataLayer
// mirror clones it into eventHubDataLayer (a direct eventHub push would duplicate it).
// operation_id links it to the Datadog InitialRender action and is the pageview dedup key.
export const useEnhancedRouteTracker = () => {
    const location = useLocation();
    const previousUriRef = useRef<string | null>(null);
    const lastTrackedPageKeyRef = useRef<string | null>(null);

    const DEBUG_TRACKING = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG_TRACKING === 'true';

    useEffect(() => {
        const pageName = location.pathname;
        const uri = `${window.location.origin}${pageName}`;
        const queryString = location.search;
        const refUri = previousUriRef.current || '';

        // Re-renders and same-page history replaces must not produce a second pageview
        const pageViewKey = `${pageName}${queryString}`;
        if (lastTrackedPageKeyRef.current === pageViewKey) return;

        const navigationType: 'spa' | 'pageLoad' = hasNavigatedBefore.value ? 'spa' : 'pageLoad';
        hasNavigatedBefore.value = true;

        const operationId = crypto.randomUUID().replace(/-/g, '');
        const start = performance.now();

        let pageLoadDuration = 0;
        if (navigationType === 'pageLoad' && window.performance) {
            const navEntries = performance.getEntriesByType('navigation');
            if (navEntries.length > 0) {
                const navTiming = navEntries[0] as PerformanceNavigationTiming;
                pageLoadDuration = navTiming.loadEventEnd > 0 ? navTiming.loadEventEnd : performance.now();
            }
        }

        if (DEBUG_TRACKING) {
            // eslint-disable-next-line no-console
            console.log('🔄 [RouteTracker] Route change detected:', {
                path: pageName,
                url: window.location.href,
                queryString,
                refUri,
                navigationType,
                timestamp: new Date().toISOString(),
                operationId
            });
        }

        // Fire after the new route has painted so document.title and timings are settled
        const animationFrameId = requestAnimationFrame(() => {
            const initialRenderDuration = performance.now() - start;

            if (DEBUG_TRACKING) {
                // eslint-disable-next-line no-console
                console.log('⏱️ [RouteTracker] Initial render complete:', {
                    path: pageName,
                    renderTime: `${Math.round(initialRenderDuration)}ms`,
                    ...(navigationType === 'pageLoad' && pageLoadDuration > 0
                        ? { pageLoadTime: `${Math.round(pageLoadDuration)}ms` }
                        : {}),
                    operationId,
                    queryString,
                    refUri,
                    navigationType,
                    timestamp: new Date().toISOString()
                });
            }

            lastTrackedPageKeyRef.current = pageViewKey;

            const virtualPageView = createSyntheticPageViewEvent({
                uri,
                refUri,
                queryString,
                navigationType,
                operationId,
                renderTimeMs: initialRenderDuration,
                pageLoadTimeMs: navigationType === 'pageLoad' ? pageLoadDuration : undefined
            });

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'virtual_page_view', ...virtualPageView });

            datadogRum.addAction('InitialRender', {
                renderTime: `${Math.round(initialRenderDuration)}ms`,
                ...(navigationType === 'pageLoad' && pageLoadDuration > 0
                    ? { pageLoadTime: `${Math.round(pageLoadDuration)}ms` }
                    : {}),
                operationId,
                queryString,
                refUri,
                navigationType
            });

            // Full URL including query string: feeds page_referrer of the next pageview
            previousUriRef.current = `${uri}${queryString}`;
        });

        return () => {
            window.cancelAnimationFrame(animationFrameId);
        };
    }, [DEBUG_TRACKING, location.pathname, location.search]);
};
